import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  StickyNote,
  FileText,
  History,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import {
  ProjectHistoryCounts,
  ProjectHistoryItem,
  ProjectHistoryKind,
} from "services/project-history.service";
import { ProjectHistoryTimelineSkeleton } from "components/project/project-tab-skeletons/project-tab-skeletons";
import ProjectActivityCard from "components/project/project-activity-card";
import ProjectActivityEditModal from "components/project/project-activity-edit-modal";
import { formatPrettyDate } from "utils/helperFunctions";
import {
  patchProjectActivity,
  ProjectActivity,
} from "services/project-activities.service";
import { toast } from "sonner";

type HistoryFilter = "all" | ProjectHistoryKind;

const FILTER_SKELETON_MS = 160;

const FILTER_TABS: Array<{
  id: HistoryFilter;
  labelKey: string;
  defaultLabel: string;
  countKey?: keyof ProjectHistoryCounts;
}> = [
  { id: "all", labelKey: "project_history_all", defaultLabel: "All" },
  {
    id: "activity",
    labelKey: "project_history_activities",
    defaultLabel: "Activities",
    countKey: "activity",
  },
  {
    id: "note",
    labelKey: "project_history_notes",
    defaultLabel: "Notes",
    countKey: "note",
  },
  {
    id: "email",
    labelKey: "project_history_emails",
    defaultLabel: "Emails",
    countKey: "email",
  },
  {
    id: "file",
    labelKey: "project_history_files",
    defaultLabel: "Files",
    countKey: "file",
  },
  {
    id: "changelog",
    labelKey: "project_history_changelog",
    defaultLabel: "Changelog",
    countKey: "changelog",
  },
];

function formatWhen(ts: number | null | undefined): string {
  if (!ts) return "—";
  return formatPrettyDate(ts, "lt-LT", true);
}

function TimelineIcon({ kind }: { kind: ProjectHistoryKind }) {
  const cls = "h-4 w-4";
  switch (kind) {
    case "activity":
      return <Phone className={cls} />;
    case "note":
      return <StickyNote className={cls} />;
    case "email":
      return <Mail className={cls} />;
    case "file":
      return <FileText className={cls} />;
    case "changelog":
      return <History className={cls} />;
    default:
      return <History className={cls} />;
  }
}

type Props = {
  companyId: string;
  projectId: string;
  items: ProjectHistoryItem[];
  counts: ProjectHistoryCounts;
  loading?: boolean;
  onChanged?: () => void;
};

export default function ProjectHistoryPanel({
  companyId,
  projectId,
  items,
  counts,
  loading,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const [displayFilter, setDisplayFilter] = useState<HistoryFilter>("all");
  const [filterPending, setFilterPending] = useState(false);
  const [editing, setEditing] = useState<ProjectActivity | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (filter === displayFilter) return;
    setFilterPending(true);
    const timer = window.setTimeout(() => {
      setDisplayFilter(filter);
      setFilterPending(false);
    }, FILTER_SKELETON_MS);
    return () => window.clearTimeout(timer);
  }, [filter, displayFilter]);

  const filtered = useMemo(() => {
    if (displayFilter === "all") return items;
    return items.filter((h) => h.kind === displayFilter);
  }, [items, displayFilter]);

  const showListSkeleton = loading || filterPending;

  const toggleDone = async (activity: ProjectActivity) => {
    setBusyId(activity.id);
    try {
      await patchProjectActivity(companyId, projectId, activity.id, {
        isDone: !activity.isDone,
      });
      onChanged?.();
    } catch {
      toast.error(
        t("project_activity_save_failed", {
          defaultValue: "Could not save activity.",
        })
      );
    } finally {
      setBusyId(null);
    }
  };

  const renderItem = (item: ProjectHistoryItem) => {
    const userLabel =
      item.user?.name || t("unknown_user", { defaultValue: "Unknown" });

    if (item.kind === "note") {
      return (
        <div className="rounded-md border border-amber-200 bg-[#fffbeb] px-3 py-2.5">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[var(--p-color-text-secondary)]">
            <span className="font-semibold text-[var(--p-color-text)]">
              {userLabel}
            </span>
            <span>·</span>
            <span>{formatWhen(item.at)}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-[var(--p-color-text)]">
            {String(item.data.text || "")}
          </p>
        </div>
      );
    }

    if (item.kind === "activity") {
      const activity = item.data as unknown as ProjectActivity;
      return (
        <ProjectActivityCard
          activity={activity}
          busy={busyId === activity.id}
          onToggleDone={() => toggleDone(activity)}
          onEdit={() => setEditing(activity)}
        />
      );
    }

    if (item.kind === "email") {
      const subject = String(item.data.subject || item.data.cardTitle || "Email");
      const preview = item.data.preview ? String(item.data.preview) : "";
      const cardNumber = item.data.cardNumber as number | null;
      return (
        <div className="rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2.5">
          <div className="text-sm font-semibold text-[var(--p-color-text)]">
            {subject}
          </div>
          <div className="text-xs text-[var(--p-color-text-secondary)]">
            {userLabel || String(item.data.fromEmail || "")} ·{" "}
            {formatWhen(item.at)}
            {item.data.type === "inbound"
              ? ` · ${t("project_history_inbound", { defaultValue: "Received" })}`
              : ` · ${t("project_history_sent", { defaultValue: "Sent" })}`}
          </div>
          {preview && (
            <p className="mt-1 line-clamp-3 text-sm text-[var(--p-color-text-secondary)]">
              {preview}
            </p>
          )}
          {cardNumber != null && (
            <Link
              to={`/app/${companyId}/desk/inbox?card=${cardNumber}`}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[var(--p-color-text-link)]"
            >
              {t("project_history_open_desk", {
                defaultValue: "Open in Desk",
              })}{" "}
              #{cardNumber}
              <ExternalLink size={12} />
            </Link>
          )}
        </div>
      );
    }

    if (item.kind === "file") {
      return (
        <div className="rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2.5">
          <div className="text-sm font-semibold text-[var(--p-color-text)]">
            {String(item.data.name || "File")}
          </div>
          <div className="text-xs text-[var(--p-color-text-secondary)]">
            {userLabel} · {formatWhen(item.at)}
          </div>
        </div>
      );
    }

    if (item.kind === "changelog") {
      const lines = Array.isArray(item.data.lines) ? item.data.lines : [];
      const action = String(item.data.action || "update");
      return (
        <div className="rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2.5">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-700">
              {lines.length
                ? t("project_history_n_changes", {
                    count: lines.length,
                    defaultValue: "{{count}} changes",
                  })
                : action}
            </span>
            <span className="text-xs text-[var(--p-color-text-secondary)]">
              {userLabel} · {formatWhen(item.at)}
            </span>
          </div>
          {lines.length > 0 ? (
            <ul className="space-y-1 text-sm text-[var(--p-color-text)]">
              {lines.map(
                (line: {
                  field: string;
                  label: string;
                  from: unknown;
                  to: unknown;
                }) => (
                  <li key={line.field}>
                    <span className="font-medium">{line.label}:</span>{" "}
                    <span className="text-[var(--p-color-text-secondary)]">
                      {line.from != null ? String(line.from) : "—"}
                    </span>
                    {" → "}
                    <span>{line.to != null ? String(line.to) : "—"}</span>
                  </li>
                )
              )}
            </ul>
          ) : (
            <p className="text-sm text-[var(--p-color-text-secondary)]">
              {String(item.data.description || action)}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown size={16} className="shrink-0" />
        ) : (
          <ChevronRight size={16} className="shrink-0" />
        )}
        <span className="text-sm font-semibold text-[var(--p-color-text)]">
          {t("project_history_title", { defaultValue: "History" })}
        </span>
        <span className="rounded-full bg-[var(--p-color-bg-surface-secondary)] px-2 py-0.5 text-xs font-medium text-[var(--p-color-text-secondary)]">
          {counts.all}
        </span>
      </button>
      {open && (
        <div className="border-t border-[var(--p-color-border)]">
          <div className="flex gap-1 overflow-x-auto border-b border-[var(--p-color-border)] px-3 py-2">
            {FILTER_TABS.map((tab) => {
              const count =
                tab.countKey != null ? counts[tab.countKey] : counts.all;
              const selected = filter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setFilter(tab.id)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-150 ${
                    selected
                      ? "bg-[var(--p-color-bg-fill-brand)] text-white"
                      : "bg-[var(--p-color-bg-surface-secondary)] text-[var(--p-color-text-secondary)] hover:text-[var(--p-color-text)]"
                  }`}
                >
                  {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
                  {tab.id !== "all" && ` (${count})`}
                </button>
              );
            })}
          </div>

          <div className="px-4 py-4 min-h-[8rem]">
            {showListSkeleton ? (
              <ProjectHistoryTimelineSkeleton rows={4} />
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--p-color-text-secondary)] project-tab-fade-in">
                {t("project_history_empty", {
                  defaultValue: "No history for this filter yet.",
                })}
              </p>
            ) : (
              <ul
                key={displayFilter}
                className="relative space-y-4 pl-8 project-tab-fade-in before:absolute before:bottom-2 before:left-[11px] before:top-2 before:w-px before:border-l before:border-dashed before:border-[var(--p-color-border)]"
              >
                {filtered.map((item) => (
                  <li key={item.id} className="relative">
                    <span className="absolute -left-8 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] text-[var(--p-color-text-secondary)]">
                      <TimelineIcon kind={item.kind} />
                    </span>
                    {renderItem(item)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <ProjectActivityEditModal
        open={!!editing}
        companyId={companyId}
        projectId={projectId}
        activity={editing}
        onClose={() => setEditing(null)}
        onSaved={() => onChanged?.()}
      />
    </div>
  );
}
